import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Barcode,
  ChevronDown,
  ChevronLeft,
  Keyboard,
  X,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useReceivingStore } from './ReceivingStore';
import NumberPadSheet, { needsPagePad } from '../../components/NumberPadSheet';
import { useVisualViewportHeight } from './receivingLayout';
import { extractUniqueEpcTags } from './receivedUtils';
import { effectiveBoxUnits } from './bulkReceiveModel';
import {
  applyLateTagRule,
  buildSkuReceiveRows,
  dedupeTags,
  getDefaultShipmentKey,
  hasUnresolvedLateTags,
  overReceiptQuantity,
  planManualAllocation,
  resolveShipmentKey,
  retryableSaveItems,
  shipmentMetadataReady,
  shipmentGroupsForPo,
  shipmentUnreceivedUnits,
  splitByRoom,
  splitEpcsAcrossBoxes,
  splitValidatedTags,
} from './skuReceiveModel';

const CONCURRENCY = 4;

function poLabel(po) {
  return `PO ${po?.poNumber || String(po?.id || '').replace(/^PO\s*/i, '')}`;
}

function shipmentLabel(shipment) {
  return `Shipment ${shipment?.number || ''}`.trim();
}

function methodLabel(method) {
  if (!method) return 'Shipping method unknown';
  return `${method.charAt(0)}${method.slice(1).toLowerCase()}`;
}

function formatDate(value) {
  if (!value) return '';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : '';
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString('en-US');
}

function boxAllocationText(allocations) {
  if (!allocations.length) return 'Boxes chosen after you pick a shipment';
  return `Fills ${allocations.map((item) => `#${item.box.boxNumber} · ${formatNumber(item.addedUnits)}`).join(', ')}`;
}

function SkuReceiveWorkspace({
  poId,
  initialShipmentKey = null,
  initialMode = 'type',
  onClose,
}) {
  const visualViewportHeight = useVisualViewportHeight();
  const {
    pos,
    ensurePoLoaded,
    refreshPo,
    captureScan,
    manualReceiveBox,
    overrideReceiveBox,
    amendBoxUnits,
  } = useReceivingStore();
  const storedPo = useMemo(
    () => pos.find((candidate) => String(candidate.id) === String(poId)) || null,
    [poId, pos],
  );
  const [loadedPo, setLoadedPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const po = storedPo || loadedPo;
  const [shipmentKey, setShipmentKey] = useState(null);
  const [mode, setMode] = useState(initialMode === 'scan' ? 'scan' : 'type');
  const [activeSku, setActiveSku] = useState('');
  const [manualBySku, setManualBySku] = useState({});
  const [tagsBySku, setTagsBySku] = useState({});
  const [heldTags, setHeldTags] = useState([]);
  const [confirmLateId, setConfirmLateId] = useState(null);
  const [scanInput, setScanInput] = useState('');
  const [scanKeyboard, setScanKeyboard] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanNotices, setScanNotices] = useState([]);
  const [sheet, setSheet] = useState(null);
  const [detailsSku, setDetailsSku] = useState('');
  const [fixBox, setFixBox] = useState(null);
  const [fixCount, setFixCount] = useState('');
  const [fixReason, setFixReason] = useState('');
  const [overConfirmed, setOverConfirmed] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveItems, setSaveItems] = useState([]);
  const [succeededIds, setSucceededIds] = useState(() => new Set());
  const [failedIds, setFailedIds] = useState(() => new Set());
  const [saveError, setSaveError] = useState('');
  const [discardOpen, setDiscardOpen] = useState(false);
  const captureRef = useRef(null);
  const rowInputRefs = useRef({});
  const [pagePad] = useState(needsPagePad);
  const [padSku, setPadSku] = useState('');
  const padAnchorRef = useRef(null);
  const closePad = useCallback(() => setPadSku(''), []);
  const initializedRef = useRef(false);
  const capturedScanIdsRef = useRef(new Set());

  useEffect(() => {
    const htmlOverflow = document.documentElement.style.overflow;
    const bodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = htmlOverflow;
      document.body.style.overflow = bodyOverflow;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ensurePoLoaded(poId)
      .then((loaded) => {
        if (!cancelled) setLoadedPo(loaded || null);
      })
      .catch(() => {
        if (!cancelled) setLoadedPo(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ensurePoLoaded, poId]);

  const shipments = useMemo(() => shipmentGroupsForPo(po), [po]);
  const arrivedOpenShipments = useMemo(
    () =>
      shipments.filter(
        (shipment) => shipment.arrival === 'arrived' && shipmentUnreceivedUnits(shipment) > 0,
      ),
    [shipments],
  );

  useEffect(() => {
    if (!po || initializedRef.current || !shipmentMetadataReady(po)) return;
    initializedRef.current = true;
    let linkedShipment = null;
    if (!initialShipmentKey && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const linkedPo = String(params.get('po') || '').replace(/^PO\s*/i, '').trim().toUpperCase();
      const currentPo = String(po.poNumber || po.id || '')
        .replace(/^PO\s*/i, '')
        .trim()
        .toUpperCase();
      if (linkedPo && linkedPo === currentPo) linkedShipment = params.get('shipment');
    }
    const requestedKey = resolveShipmentKey(po, initialShipmentKey || linkedShipment);
    const requested = shipments.find((shipment) => shipment.key === requestedKey);
    const selected =
      requested?.arrival === 'arrived' && shipmentUnreceivedUnits(requested) > 0
        ? requested
        : arrivedOpenShipments.find((shipment) => shipment.key === getDefaultShipmentKey(po));
    if (selected) setShipmentKey(selected.key);
  }, [arrivedOpenShipments, initialShipmentKey, po, shipments]);

  const currentShipment = shipments.find((shipment) => shipment.key === shipmentKey) || null;
  const rows = useMemo(
    () => buildSkuReceiveRows(po, shipmentKey),
    [po, shipmentKey],
  );
  const rowsBySku = useMemo(() => new Map(rows.map((row) => [row.sku, row])), [rows]);
  const activeRow = rowsBySku.get(activeSku) || rows[0] || null;

  useEffect(() => {
    if (!activeRow || activeSku === activeRow.sku) return;
    setActiveSku(activeRow.sku);
  }, [activeRow, activeSku]);

  const reviewLines = useMemo(
    () =>
      rows.map((row) => {
        const manualQuantity = Number(manualBySku[row.sku]) || 0;
        const pendingTags = tagsBySku[row.sku] || [];
        const countOver = overConfirmed[row.sku] === true;
        const overTags = countOver
          ? heldTags
              .filter((item) => item.kind === 'over' && item.sku === row.sku)
              .flatMap((item) => item.epcs)
          : [];
        const scanTags = [...pendingTags, ...overTags];
        const scanPlan = splitEpcsAcrossBoxes(row.boxes, scanTags, {
          allowOverReceipt: countOver,
        });
        const manualAllocations = planManualAllocation(
          row.boxes,
          manualQuantity,
          row.scannedWholePo,
        );
        const plannedScanCount = scanPlan.allocations.reduce(
          (sum, item) => sum + item.epcs.length,
          0,
        );
        const totalPending = manualQuantity + plannedScanCount;
        return {
          ...row,
          manualQuantity,
          manualAllocations,
          scanTags,
          scanAllocations: scanPlan.allocations,
          unallocatedEpcs: scanPlan.unallocatedEpcs,
          overQuantity: overReceiptQuantity(totalPending, row.unreceived),
          totalPending,
          hasEntry: manualQuantity > 0 || pendingTags.length > 0 || overTags.length > 0,
        };
      }),
    [heldTags, manualBySku, overConfirmed, rows, tagsBySku],
  );

  const activeReviewLines = reviewLines.filter((line) => line.hasEntry);
  const receivingTotal = reviewLines.reduce((sum, line) => {
    const pendingTags = tagsBySku[line.sku] || [];
    return sum + (Number(manualBySku[line.sku]) || 0) + pendingTags.length;
  }, 0);
  const receivingSkuCount = reviewLines.filter(
    (line) => (Number(manualBySku[line.sku]) || 0) > 0 || (tagsBySku[line.sku] || []).length > 0,
  ).length;
  const pendingWork =
    receivingTotal > 0 ||
    heldTags.some((item) => item.epcs.length > 0) ||
    extractUniqueEpcTags(scanInput).length > 0;
  const hasAmbiguousShipments = rows.some((row) => row.ambiguousShipmentKeys.length > 1);
  const anyAmbiguous = !shipmentKey && hasAmbiguousShipments;
  const selectionRequired = anyAmbiguous;

  useEffect(() => {
    if (anyAmbiguous) setSheet('shipments');
  }, [anyAmbiguous]);

  const handleClose = useCallback(() => {
    if (saving) return;
    if (pendingWork) {
      setDiscardOpen(true);
      return;
    }
    onClose();
  }, [onClose, pendingWork, saving]);

  const chooseShipment = (shipment) => {
    setShipmentKey(shipment.key);
    setSheet(null);
  };

  const handleManualChange = (sku, value) => {
    if ((tagsBySku[sku] || []).length > 0) {
      toast.info('Clear scanned tags before entering a manual quantity.', {
        position: 'bottom-right',
      });
      return;
    }
    const digits = value.replace(/\D/g, '');
    setManualBySku((current) => ({ ...current, [sku]: digits }));
  };

  const handleScanValidation = async () => {
    if (!po?.poDbId || !scanInput.trim()) return;
    const epcs = extractUniqueEpcTags(scanInput);
    if (!epcs.length) {
      toast.info('No EPC tags found in this scan.', { position: 'bottom-right' });
      return;
    }
    const validationBox = (po.boxes || []).find(
      (box) => box.inboundType === 'box' && Number(box.id) > 0,
    );
    setScanBusy(true);
    setScanNotices([]);
    try {
      let validation;
      if (validationBox) {
        const { data } = await axios.post('/api/v1/box-received-logs/validate-epcs', {
          box_id: Number(validationBox.id),
          epc_hex_tags: epcs,
        });
        validation = data;
      } else {
        const { data } = await axios.post('/api/v1/po-received-logs/validate-epcs', {
          po_id: po.poDbId,
          epc_hex_tags: epcs,
        });
        const validTags = (data.byProduct || []).flatMap((product) =>
          (product.validEpcs || []).map((hexEpc) => ({
            hex_epc: hexEpc,
            product_id: product.product_id,
          })),
        );
        const duplicateTags = data.duplicates || [];
        const known = new Set([
          ...validTags.map((tag) => tag.hex_epc),
          ...duplicateTags.map((tag) => tag.hexEpc || tag.hex_epc),
        ]);
        validation = {
          classification: {
            newlyReceived: [],
            newlyReceivedOtherSku: validTags,
            alreadyInBox: duplicateTags,
            receivedElsewhere: [],
            receivedBoxUnknown: [],
            notOnPo: epcs.filter((tag) => !known.has(tag)),
          },
        };
      }

      const productIdToSku = new Map(
        (po.boxes || [])
          .filter((box) => box.productId)
          .map((box) => [String(box.productId), box.sku]),
      );
      (po.lineItems || []).forEach((line) => {
        if (line.productId) productIdToSku.set(String(line.productId), line.sku);
      });
      const sorted = splitValidatedTags({
        validation,
        productIdToSku,
        targetProductId: validationBox?.productId,
      });
      const allPendingTags = [
        ...Object.values(tagsBySku).flat(),
        ...heldTags.flatMap((item) => item.epcs),
      ];
      const batchSeen = new Set(allPendingTags);
      const notices = [];
      if (sorted.alreadySaved.length) {
        notices.push(`${sorted.alreadySaved.length} already saved, not counted.`);
      }
      if (sorted.notOnPo.length) {
        notices.push(`${sorted.notOnPo.length} not on this PO.`);
      }
      let rereadCount = 0;
      for (const [sku, skuTags] of sorted.bySku) {
        const row = rowsBySku.get(sku);
        const deduped = dedupeTags(skuTags, batchSeen);
        deduped.unique.forEach((tag) => batchSeen.add(tag));
        rereadCount += deduped.duplicates.length;
        if (!deduped.unique.length) continue;
        if (!row) {
          setHeldTags((current) => [
            ...current,
            {
              id: `${sku}-${Date.now()}-${Math.random()}`,
              kind: 'over',
              sku,
              epcs: deduped.unique,
              decision: 'leave',
              reason: 'This SKU is not part of the selected shipment.',
            },
          ]);
          continue;
        }
        setManualBySku((current) => {
          if (!current[sku]) return current;
          const next = { ...current };
          delete next[sku];
          return next;
        });
        const rule = applyLateTagRule({
          epcs: deduped.unique,
          unreceived: row.unreceived,
          pendingCount: (tagsBySku[sku] || []).length,
          tagGap: row.tagGap,
        });
        if (rule.counted.length) {
          setTagsBySku((current) => ({
            ...current,
            [sku]: [...(current[sku] || []), ...rule.counted],
          }));
        }
        if (rule.late.length) {
          setHeldTags((current) => [
            ...current,
            {
              id: `${sku}-late-${Date.now()}-${Math.random()}`,
              kind: 'late',
              sku,
              epcs: rule.late,
            },
          ]);
        }
        if (rule.over.length) {
          setHeldTags((current) => [
            ...current,
            {
              id: `${sku}-over-${Date.now()}-${Math.random()}`,
              kind: 'over',
              sku,
              epcs: rule.over,
              decision: 'leave',
              reason: 'These tags exceed the selected shipment’s unreceived quantity.',
            },
          ]);
        }
      }
      if (rereadCount) notices.push(`${rereadCount} tag(s) already in this session, counted once.`);
      setScanNotices(notices);
      setScanInput('');
      if (notices.length) toast.info(notices.join(' '), { position: 'bottom-right' });
    } catch (error) {
      toast.error(
        error?.response?.data?.error || error?.message || 'Could not validate EPC tags.',
        { position: 'bottom-right' },
      );
    } finally {
      setScanBusy(false);
    }
  };

  const lateTagsHeld = hasUnresolvedLateTags(heldTags);

  const resolveLateTags = (item, action) => {
    setConfirmLateId(null);
    setHeldTags((current) => current.filter((held) => held.id !== item.id));
    if (action !== 'count') return;
    const row = rowsBySku.get(item.sku);
    const { counted, over } = splitByRoom({
      epcs: item.epcs,
      unreceived: row?.unreceived,
      pendingCount: (tagsBySku[item.sku] || []).length,
    });
    if (counted.length) {
      setTagsBySku((current) => ({
        ...current,
        [item.sku]: [...(current[item.sku] || []), ...counted],
      }));
    }
    if (over.length) {
      setHeldTags((current) => [
        ...current,
        {
          id: `${item.sku}-over-${Date.now()}-${Math.random()}`,
          kind: 'over',
          sku: item.sku,
          epcs: over,
          decision: 'leave',
          reason: 'These tags exceed the selected shipment’s unreceived quantity.',
        },
      ]);
    }
  };

  const savedTagGapForSku = (sku) => {
    const row = rowsBySku.get(sku);
    const saved = Number(row?.savedWholePo) || 0;
    const tags = Number(row?.scannedWholePo) || 0;
    return { saved, tags, gap: Math.max(0, saved - tags) };
  };

  const changeManualOverConfirmation = (sku, checked) => {
    setOverConfirmed((current) => ({ ...current, [sku]: checked }));
  };

  const buildSaveItems = () => {
    const items = [];
    for (const line of activeReviewLines) {
      if (line.manualQuantity > 0) {
        line.manualAllocations.forEach((allocation) => {
          items.push({
            ...allocation,
            id: `${line.sku}:manual:${allocation.id}`,
            sku: line.sku,
            kind: allocation.action,
            expectedUnits: Number(allocation.box.expectedUnits) || 0,
            source: 'manual',
          });
        });
      }
      line.scanAllocations.forEach((allocation) => {
        items.push({
          ...allocation,
          id: `${line.sku}:scan:${allocation.id}`,
          sku: line.sku,
          kind: 'captureScan',
          expectedUnits: Number(allocation.box.expectedUnits) || 0,
          source: 'scan',
        });
      });
    }
    return items;
  };

  const saveItemsInWorkers = async (items, allItems) => {
    if (!items.length) return;
    setSaving(true);
    setSaveError('');
    setFailedIds(new Set());
    let cursor = 0;
    const completedNow = [];
    const failedNow = [];
    const worker = async () => {
      while (cursor < items.length) {
        const item = items[cursor];
        cursor += 1;
        try {
          if (item.kind === 'captureScan') {
            if (!capturedScanIdsRef.current.has(item.id)) {
              await captureScan(po.id, item.box.id, {
                epcs: item.epcs,
                matchedCount: item.epcs.length,
                totalScanned: item.epcs.length,
                poMatched: item.epcs.length,
                allowOverReceipt: item.allowOverReceipt === true,
              });
              capturedScanIdsRef.current.add(item.id);
            }
            if (item.targetUnits < item.expectedUnits) {
              await overrideReceiveBox(po.id, item.box.id, item.targetUnits);
            }
          } else if (item.kind === 'manualReceiveBox') {
            await manualReceiveBox(po.id, item.box.id, item.targetUnits);
          } else {
            await overrideReceiveBox(po.id, item.box.id, item.targetUnits);
          }
          completedNow.push(String(item.id));
        } catch (error) {
          failedNow.push({ item, error });
        }
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, items.length) }, () => worker()),
    );
    const nextSucceeded = new Set(succeededIds);
    completedNow.forEach((id) => nextSucceeded.add(id));
    setSucceededIds(nextSucceeded);
    setFailedIds(new Set(failedNow.map(({ item }) => String(item.id))));
    setSaveItems(allItems);

    if (failedNow.length) {
      setSaveError(
        `${failedNow.length} line(s) did not save. Retry sends only the failed box actions.`,
      );
      setSaving(false);
      toast.error('Some receiving lines failed to save.', { position: 'bottom-right' });
      return;
    }

    const completedSkus = new Set(
      allItems
        .map((item) => item.sku)
        .filter((sku) =>
          allItems
            .filter((item) => item.sku === sku)
            .every((item) => nextSucceeded.has(String(item.id))),
        ),
    );
    setManualBySku((current) => {
      const next = { ...current };
      completedSkus.forEach((sku) => delete next[sku]);
      return next;
    });
    setTagsBySku((current) => {
      const next = { ...current };
      completedSkus.forEach((sku) => delete next[sku]);
      return next;
    });
    setHeldTags((current) =>
      current.filter(
        (item) => !(item.kind === 'over' && item.decision === 'count' && completedSkus.has(item.sku)),
      ),
    );
    setOverConfirmed((current) => {
      const next = { ...current };
      completedSkus.forEach((sku) => delete next[sku]);
      return next;
    });

    const savedTotal = allItems.reduce((sum, item) => sum + item.addedUnits, 0);
    let refreshed = po;
    try {
      refreshed = await refreshPo(po);
      if (refreshed) setLoadedPo(refreshed);
    } catch (error) {
      toast.warn('Saved, but the PO could not be refreshed.', { position: 'bottom-right' });
    }
    const refreshedRows = buildSkuReceiveRows(refreshed || po, shipmentKey);
    const lastSku = allItems[allItems.length - 1]?.sku;
    const lastIndex = refreshedRows.findIndex((row) => row.sku === lastSku);
    const nextRow = refreshedRows
      .slice(lastIndex + 1)
      .concat(refreshedRows.slice(0, lastIndex + 1))
      .find((row) => row.unreceived > 0);
    if (nextRow) setActiveSku(nextRow.sku);
    setReviewOpen(false);
    setSaveItems([]);
    setSucceededIds(new Set());
    setFailedIds(new Set());
    capturedScanIdsRef.current = new Set();
    setSaving(false);
    const currentShipmentLabel = currentShipment ? shipmentLabel(currentShipment) : 'arrived shipments';
    toast.success(
      `Saved +${formatNumber(savedTotal)} ${allItems.map((item) => item.sku).filter((sku, index, list) => list.indexOf(sku) === index).join(', ')} · ${currentShipmentLabel}${nextRow ? ` · Next: ${nextRow.sku}` : ''}`,
      { position: 'bottom-right' },
    );
  };

  const [reviewOpen, setReviewOpen] = useState(false);

  const handleSave = async () => {
    if (selectionRequired || lateTagsHeld) return;
    const lines = activeReviewLines;
    const overUnconfirmed = lines.some(
      (line) => line.overQuantity > 0 && overConfirmed[line.sku] !== true,
    );
    if (overUnconfirmed) return;
    const allItems = buildSaveItems();
    const retryItems = saveItems.length
      ? retryableSaveItems(saveItems, succeededIds)
      : allItems;
    await saveItemsInWorkers(retryItems, saveItems.length ? saveItems : allItems);
  };

  const handleAmend = async () => {
    if (!fixBox) return;
    const count = parseInt(fixCount, 10);
    if (Number.isNaN(count) || count < 0) {
      toast.error('Enter the corrected unit count first.', { position: 'bottom-right' });
      return;
    }
    if (!fixReason.trim()) {
      toast.error('A reason is required for post-receive amendments.', { position: 'bottom-right' });
      return;
    }
    const previous = effectiveBoxUnits(fixBox);
    if (count === previous) {
      toast.error('The corrected count matches the current count — nothing to amend.', {
        position: 'bottom-right',
      });
      return;
    }
    setSaving(true);
    try {
      await amendBoxUnits(po.id, fixBox.id, count, fixReason.trim());
      const refreshed = await refreshPo(po);
      if (refreshed) setLoadedPo(refreshed);
      setFixBox(null);
      setSheet('boxes');
      toast.success(`Box ${fixBox.boxNumber} amended (${previous} → ${count} units)`, {
        position: 'bottom-right',
      });
    } catch (error) {
      toast.error(error?.response?.data?.error || error?.message || 'Failed to amend box count.', {
        position: 'bottom-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const changeInputAndScroll = (sku, value) => {
    handleManualChange(sku, value);
    requestAnimationFrame(() =>
      rowInputRefs.current[sku]?.scrollIntoView?.({ block: 'nearest' }),
    );
  };

  const viewportStyle = {
    top: window.visualViewport?.offsetTop ?? 0,
    height: visualViewportHeight ? `${visualViewportHeight}px` : '100dvh',
  };
  const tablet = typeof window !== 'undefined' && window.innerWidth >= 700;
  const gridCols = tablet
    ? 'grid-cols-[minmax(var(--sku-w,0px),1fr)_5.5rem_6.5rem_7rem_minmax(6rem,8.5rem)]'
    : 'grid-cols-[minmax(var(--sku-w,0px),1fr)_4rem_4.5rem_5.25rem]';
  const skuCellRefs = useRef({});
  const [skuColumnWidth, setSkuColumnWidth] = useState(0);
  useLayoutEffect(() => {
    const widest = Math.max(0, ...Object.values(skuCellRefs.current).filter(Boolean).map((el) => el.scrollWidth));
    setSkuColumnWidth((current) => (current === widest ? current : widest));
  }, [rows, tablet, mode]);
  const content = (
    <div
      className="fixed inset-x-0 z-[120] flex flex-col overflow-hidden bg-[#f7f7f8] text-[#0f1115]"
      style={viewportStyle}
      role="dialog"
      aria-modal="true"
      aria-label={`Receiving ${po ? poLabel(po) : poId}`}
    >
      <header className="flex h-11 shrink-0 items-center gap-1 border-b border-[#ececef] bg-white px-3">
        <button
          type="button"
          onClick={handleClose}
          className="inline-flex h-9 shrink-0 items-center gap-0.5 rounded-md px-1.5 text-sm font-semibold text-[#4b5060] hover:bg-[#f3f3f5]"
          aria-label="Return to queue"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Queue</span>
        </button>
        <span className="mx-1 h-5 w-px shrink-0 bg-[#ececef]" />
        <span className={`min-w-0 flex-1 truncate font-semibold ${tablet ? 'text-lg' : 'text-sm'}`}>{po ? poLabel(po) : `PO ${poId}`}</span>
        {hasAmbiguousShipments ? (
          <button
            type="button"
            onClick={() => setSheet('shipments')}
            className={`inline-flex max-w-[42%] shrink-0 items-center gap-1 truncate rounded-md border px-2 py-1 text-xs font-semibold ${
              shipmentKey
                ? 'border-[#ececef] bg-white text-[#4b5060]'
                : 'border-orange-200 bg-orange-50 text-orange-800'
            }`}
            aria-label="Choose shipment"
          >
            <span className="truncate">
              {currentShipment ? `${shipmentLabel(currentShipment)} · ${methodLabel(currentShipment.method)}` : 'Pick shipment'}
            </span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          </button>
        ) : (
          <span className="max-w-[42%] shrink-0 truncate px-2 py-1 text-xs font-semibold text-[#4b5060]">
            {currentShipment ? `${shipmentLabel(currentShipment)} · ${methodLabel(currentShipment.method)}` : 'Arrived shipments'}
          </span>
        )}
        <button
          type="button"
          onClick={handleClose}
          className="ml-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[#7b8090] hover:bg-[#f3f3f5]"
          aria-label="Close receiving workspace"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[#ececef] bg-white px-3 py-2">
        <div className="inline-flex h-8 rounded-md bg-[#f3f3f5] p-0.5" role="group" aria-label="Receiving mode">
          {['type', 'scan'].map((nextMode) => (
            <button
              key={nextMode}
              type="button"
              onClick={() => setMode(nextMode)}
              className={`rounded px-3 font-semibold capitalize ${tablet ? 'text-base' : 'text-xs'} ${
                mode === nextMode ? 'bg-white text-[#0f1115] shadow-sm' : 'text-[#7b8090]'
              }`}
            >
              {nextMode === 'type' ? 'Type' : 'Scan'}
            </button>
          ))}
        </div>
        {mode === 'scan' && (
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            <Barcode className="h-4 w-4 shrink-0 text-[#7b8090]" />
            <input
              ref={captureRef}
              value={scanInput}
              onChange={(event) => setScanInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleScanValidation();
                }
              }}
              type="text"
              inputMode={scanKeyboard ? 'text' : 'none'}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              enterKeyHint="done"
              placeholder="Scan EPC tags"
              className="h-9 min-w-0 flex-1 rounded-md border border-[#ececef] bg-white px-2 font-mono text-sm focus:border-[#0f1115] focus:outline-none"
              aria-label="Scan EPC tags"
            />
            <button
              type="button"
              onClick={() => {
                setScanKeyboard((enabled) => !enabled);
                captureRef.current?.focus();
              }}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#ececef] bg-white text-[#4b5060]"
              aria-label="Type EPCs with keyboard"
              aria-pressed={scanKeyboard}
            >
              <Keyboard className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleScanValidation}
              disabled={scanBusy || !scanInput.trim()}
              className="h-9 shrink-0 rounded-md bg-[#0f1115] px-2.5 text-xs font-semibold text-white disabled:opacity-40"
            >
              {scanBusy ? 'Checking…' : 'Add tags'}
            </button>
          </div>
        )}
      </div>

      {mode === 'scan' && scanNotices.length > 0 && (
        <div className="shrink-0 border-b border-[#ececef] bg-white px-3 py-1.5 text-xs text-[#7b8090]">
          {scanNotices.join(' ')}
        </div>
      )}
      {selectionRequired && (
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-900">
          <span>Pick which shipment you’re receiving.</span>
          <button
            type="button"
            onClick={() => setSheet('shipments')}
            className="shrink-0 rounded border border-orange-200 bg-white px-2 py-1 font-semibold"
          >
            Pick shipment
          </button>
        </div>
      )}

      <div className={`flex shrink-0 items-center justify-between gap-2 px-3 py-2 font-semibold uppercase tracking-wide text-[#7b8090] ${tablet ? 'text-[15px]' : 'text-[11px]'}`}>
        <span>{shipmentKey
          ? `${shipmentLabel(currentShipment)} · ${rows.length} ${rows.length === 1 ? 'SKU' : 'SKUs'}`
          : `Arrived shipments · ${rows.length} ${rows.length === 1 ? 'SKU' : 'SKUs'}`}</span>
        <span>{formatNumber(rows.reduce((sum, row) => sum + row.unreceived, 0))} unreceived</span>
      </div>

      <div data-testid="receiving-rows" style={{ '--sku-w': `${skuColumnWidth}px` }} className={`min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain bg-white ${pagePad && padSku && mode === 'type' ? 'pb-[22rem]' : ''}`}>
        {loading ? (
          <div className="p-8 text-center text-sm text-[#7b8090]">Loading PO…</div>
        ) : !po ? (
          <div className="p-8 text-center text-sm text-red-700" role="alert">{`PO ${poId} not found`}</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#7b8090]">No shipment boxes to receive.</div>
        ) : (
          <>
            <div className={`grid min-w-fit ${gridCols} gap-2 border-b border-[#ececef] bg-[#fafafb] font-semibold uppercase tracking-wide text-[#7b8090] ${tablet ? 'px-4 py-2.5 text-sm' : 'px-3 py-2 text-[10px]'}`}>
              <span>SKU</span>
              <span className="text-right">Received</span>
              <span className="text-right">Unreceived</span>
              <span className="text-right">Receiving</span>
              {tablet && <span>Status</span>}
            </div>
            {rows.map((row) => {
              const manualValue = manualBySku[row.sku] || '';
              const scannedCount = (tagsBySku[row.sku] || []).length;
              const selected = activeSku === row.sku;
              const statusLabel =
                row.ambiguousShipmentKeys.length > 1 && !shipmentKey
                  ? 'Pick shipment'
                  : row.unreceived === 0
                    ? 'Done'
                    : scannedCount || Number(manualValue) > 0
                      ? 'Ready to save'
                      : row.received > 0
                        ? 'Partly received'
                        : 'Not started';
              return (
                <div
                  key={row.sku}
                  data-testid="receiving-row"
                  className={`grid min-w-fit ${gridCols} gap-2 items-center border-b border-[#f3f3f5] ${tablet ? 'px-4 py-3 text-[22px]' : 'px-3 py-2.5 text-xs'} ${selected ? 'bg-[#f3f3f5]' : 'bg-white'}`}
                  onClick={() => setActiveSku(row.sku)}
                >
                  <span ref={(el) => { skuCellRefs.current[row.sku] = el; }} className="whitespace-nowrap font-semibold text-[#0f1115]">{row.sku}</span>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setDetailsSku(row.sku);
                      setSheet('boxes');
                    }}
                    className="text-right tabular-nums text-[#4b5060] underline decoration-[#a2a6b3] underline-offset-2"
                    aria-label={`Box details for ${row.sku}`}
                  >
                    {formatNumber(row.received)}
                  </button>
                  <span className="text-right tabular-nums text-[#4b5060]">
                    {formatNumber(row.unreceived)}
                  </span>
                  <div className="flex min-w-0 justify-end">
                    {mode === 'scan' ? (
                      scannedCount > 0 ? (
                        <span className="whitespace-nowrap font-semibold text-blue-700">{scannedCount} RFID ×</span>
                      ) : (
                        <span className="text-[#a2a6b3]">0</span>
                      )
                    ) : (
                      <input
                        ref={(element) => { rowInputRefs.current[row.sku] = element; }}
                        type="text"
                        inputMode={pagePad ? 'none' : 'numeric'}
                        pattern="[0-9]*"
                        enterKeyHint="next"
                        autoComplete="off"
                        value={manualValue}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!pagePad) return;
                          const box = event.currentTarget;
                          padAnchorRef.current = box;
                          setPadSku(row.sku);
                          requestAnimationFrame(() => box.scrollIntoView?.({ block: 'center' }));
                        }}
                        onFocus={(event) => {
                          setActiveSku(row.sku);
                          event.target.scrollIntoView?.({ block: 'nearest' });
                        }}
                        onChange={(event) => changeInputAndScroll(row.sku, event.target.value)}
                        className={`w-full min-w-0 rounded-md border border-[#ececef] bg-white text-right tabular-nums focus:border-[#0f1115] focus:outline-none ${tablet ? 'h-[52px] px-4 text-2xl' : 'h-9 px-3 text-sm'}`}
                        aria-label={`Receiving for ${row.sku}`}
                      />
                    )}
                  </div>
                  {tablet && (
                    <span className="inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap text-base text-[#4b5060]" title={statusLabel}>
                      <i className={`h-2.5 w-2.5 shrink-0 rounded-full ${statusLabel === 'Done' ? 'bg-emerald-500' : statusLabel === 'Ready to save' ? 'bg-blue-500' : statusLabel === 'Pick shipment' ? 'bg-orange-400' : 'bg-[#a2a6b3]'}`} />
                      <span className="truncate">{statusLabel}</span>
                    </span>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      <footer className="flex shrink-0 items-center justify-between gap-2 border-t border-[#ececef] bg-white px-3 py-2.5">
        <div className={`min-w-0 ${tablet ? 'text-lg' : 'text-sm'}`}>
          {receivingTotal > 0 ? (
            <>
              <strong>Receiving {formatNumber(receivingTotal)}</strong>
              <span className={`ml-1 text-[#7b8090] ${tablet ? 'text-base' : 'text-xs'}`}>· {receivingSkuCount} SKU{receivingSkuCount === 1 ? '' : 's'}</span>
            </>
          ) : (
            <span className={`text-[#7b8090] ${tablet ? 'text-base' : 'text-xs'}`}>Nothing to save yet</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setReviewOpen(true)}
          disabled={!pendingWork || saving}
          className={`shrink-0 rounded-md bg-[#0f1115] font-semibold text-white disabled:opacity-40 ${tablet ? 'h-12 px-6 text-lg' : 'h-10 px-4 text-sm'}`}
        >
          Review &amp; save
        </button>
      </footer>

      {reviewOpen && (
        <div className="absolute inset-0 z-10 flex items-end justify-center bg-black/30" role="presentation">
          <section
            className="flex max-h-[88%] w-full flex-col overflow-hidden rounded-t-xl border border-[#ececef] bg-white shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sku-review-title"
          >
            <div className="shrink-0 border-b border-[#ececef] px-4 py-3">
              <h2 id="sku-review-title" className="text-base font-semibold">Review before saving</h2>
              <p className="mt-1 text-xs text-[#7b8090]">
                {po ? `${poLabel(po)} · ${currentShipment ? shipmentLabel(currentShipment) : 'Arrived shipments'}` : ''}
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
              {selectionRequired && (
                <div className="mb-3 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900">
                  <strong>Pick which shipment you’re receiving.</strong>
                  <button
                    type="button"
                    onClick={() => setSheet('shipments')}
                    className="mt-2 block rounded-md border border-orange-200 bg-white px-3 py-1.5 text-xs font-semibold"
                  >
                    Pick shipment
                  </button>
                </div>
              )}
              {activeReviewLines.map((line) => (
                <div key={line.sku} className="mb-2 rounded-lg border border-[#ececef] p-3">
                  <div className="flex items-center justify-between gap-3 text-sm font-semibold">
                    <span>{line.sku}</span>
                    <span>+{formatNumber(line.totalPending)}</span>
                  </div>
                  {line.manualQuantity > 0 && (
                    <p className="mt-1 text-xs text-[#7b8090]">
                      Typed count · {boxAllocationText(line.manualAllocations)}
                    </p>
                  )}
                  {(tagsBySku[line.sku] || []).length > 0 && (
                    <p className="mt-1 text-xs text-[#7b8090]">
                      RFID tags · {boxAllocationText(line.scanAllocations)}
                    </p>
                  )}
                  {line.overQuantity > 0 && (
                    <label className="mt-3 flex items-start gap-2 rounded-md bg-orange-50 p-2 text-xs text-orange-900">
                      <input
                        type="checkbox"
                        checked={overConfirmed[line.sku] === true}
                        onChange={(event) => changeManualOverConfirmation(line.sku, event.target.checked)}
                      />
                      <span>I counted {formatNumber(line.overQuantity)} more than this shipment expects. Save as an over-receipt.</span>
                    </label>
                  )}
                </div>
              ))}
              {heldTags.filter((item) => item.kind === 'late').map((item) => {
                const { saved, tags, gap } = savedTagGapForSku(item.sku);
                return (
                  <div key={item.id} className="mb-2 rounded-lg border border-orange-200 bg-orange-50 p-3">
                    <strong className="text-sm">{item.epcs.length} {item.sku} tags held, not counted</strong>
                    <p className="mt-1 text-xs text-orange-900">
                      {item.sku}: {formatNumber(saved)} units are saved but only {formatNumber(tags)} have RFID tags ({formatNumber(gap)} missing). Those missing tags could be in this scan, and Eadin can't tell which tags came from which box. Save is off until you choose.
                    </p>
                    {confirmLateId === item.id ? (
                      <div className="mt-2 rounded-md border border-orange-300 bg-white p-2">
                        <p className="text-xs font-semibold text-orange-900">
                          {item.sku} has {formatNumber(gap)} saved units with no tag on record. Count these {formatNumber(item.epcs.length)} as new units?
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => resolveLateTags(item, 'count')}
                            disabled={saving}
                            className="h-8 rounded-md bg-orange-700 px-3 text-xs font-semibold text-white"
                          >
                            Yes, count {formatNumber(item.epcs.length)} as new units
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmLateId(null)}
                            className="h-8 rounded-md border border-orange-300 bg-white px-3 text-xs font-semibold text-orange-900"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setConfirmLateId(item.id)}
                          disabled={saving}
                          className="h-8 rounded-md border border-orange-300 bg-white px-3 text-xs font-semibold text-orange-900"
                        >
                          None are from an earlier box: count them
                        </button>
                        <button
                          type="button"
                          onClick={() => resolveLateTags(item, 'remove')}
                          disabled={saving}
                          className="h-8 rounded-md border border-orange-300 bg-white px-3 text-xs font-semibold text-orange-900"
                        >
                          Remove them and scan again
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {heldTags.filter((item) => item.kind === 'over').map((item) => (
                <div key={item.id} className="mb-2 rounded-lg border border-orange-200 bg-orange-50 p-3">
                  <strong className="text-sm">{item.epcs.length} {item.sku} tags not counted</strong>
                  <p className="mt-1 text-xs text-orange-900">{item.reason || 'These tags exceed the selected shipment’s unreceived quantity.'}</p>
                  {!item.reason?.includes('not part') && (
                    <label className="mt-2 flex items-start gap-2 text-xs text-orange-900">
                      <input
                        type="checkbox"
                        checked={overConfirmed[item.sku] === true}
                        onChange={(event) => changeManualOverConfirmation(item.sku, event.target.checked)}
                      />
                      <span>Count as an over-receipt on {currentShipment ? shipmentLabel(currentShipment) : 'this shipment'}.</span>
                    </label>
                  )}
                </div>
              ))}
              {scanNotices.map((notice) => (
                <p key={notice} className="mb-2 text-xs text-[#7b8090]">{notice}</p>
              ))}
              {saveError && (
                <div role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  <p>{saveError}</p>
                  <p className="mt-1 text-xs">Succeeded box actions will not be sent again.</p>
                </div>
              )}
              {activeReviewLines.length === 0 && heldTags.length === 0 && (
                <p className="text-sm text-[#7b8090]">Nothing entered yet.</p>
              )}
            </div>
            <div className="flex shrink-0 justify-end gap-2 border-t border-[#ececef] px-4 py-3">
              <button
                type="button"
                onClick={() => setReviewOpen(false)}
                disabled={saving}
                className="h-9 rounded-md border border-[#ececef] px-3 text-sm font-medium"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={
                  saving ||
                  selectionRequired ||
                  lateTagsHeld ||
                  activeReviewLines.length === 0 ||
                  activeReviewLines.some((line) => line.overQuantity > 0 && overConfirmed[line.sku] !== true)
                }
                className="h-9 rounded-md bg-[#0f1115] px-4 text-sm font-semibold text-white disabled:opacity-40"
              >
                {saving ? 'Saving…' : failedIds.size ? 'Retry failed lines' : `Save${receivingTotal ? ` +${formatNumber(receivingTotal)} units` : ''}`}
              </button>
            </div>
          </section>
        </div>
      )}

      {sheet === 'shipments' && (
        <div className="absolute inset-0 z-20 flex items-end justify-center bg-black/30" role="presentation">
          <section className="max-h-[88%] w-full overflow-y-auto rounded-t-xl border border-[#ececef] bg-white p-4 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="shipment-picker-title">
            <h2 id="shipment-picker-title" className="text-base font-semibold">Which shipment are you receiving?</h2>
            <p className="mt-1 text-xs text-[#7b8090]">Units only count toward the arrived shipment you pick.</p>
            <div className="mt-3 space-y-2">
              {arrivedOpenShipments.map((shipment) => {
                const open = shipment.boxes.filter(
                  (box) => Number(box.expectedUnits) > effectiveBoxUnits(box),
                ).length;
                const selected = shipment.key === shipmentKey;
                return (
                  <button
                    key={shipment.key}
                    type="button"
                    onClick={() => chooseShipment(shipment)}
                    className={`flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-left ${selected ? 'border-[#0f1115] bg-[#fafafb]' : 'border-[#ececef]'}`}
                  >
                    <span className="min-w-0">
                      <strong className="block truncate text-sm">{shipmentLabel(shipment)} · {methodLabel(shipment.method)}{selected ? ' ✓' : ''}</strong>
                      <span className="mt-1 block truncate text-xs text-[#7b8090]">
                        {shipment.tracking || 'No tracking'}
                        {shipment.deliveredAt
                          ? ` · Arrived ${formatDate(shipment.deliveredAt)}`
                          : shipment.shipDate
                            ? ` · Shipped ${formatDate(shipment.shipDate)}`
                            : ' · Arrived'}
                      </span>
                    </span>
                    <span className="shrink-0 rounded bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-800">
                      {open} boxes open
                    </span>
                  </button>
                );
              })}
              {!arrivedOpenShipments.length && <p className="text-sm text-[#7b8090]">No arrived shipments with unreceived units.</p>}
            </div>
            <button type="button" onClick={() => setSheet(null)} className="mt-4 h-9 w-full rounded-md border border-[#ececef] text-sm font-medium">Close</button>
          </section>
        </div>
      )}

      {sheet === 'boxes' && (
        <div className="absolute inset-0 z-20 flex items-end justify-center bg-black/30" role="presentation">
          <section className="max-h-[88%] w-full overflow-y-auto rounded-t-xl border border-[#ececef] bg-white p-4 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="box-details-title">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 id="box-details-title" className="text-base font-semibold">Boxes · {detailsSku}</h2>
                <p className="mt-1 text-xs text-[#7b8090]">{currentShipment ? shipmentLabel(currentShipment) : 'Arrived shipments'}</p>
              </div>
              <button type="button" onClick={() => setSheet(null)} aria-label="Close box details" className="rounded p-1 text-[#7b8090]"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 divide-y divide-[#f3f3f5]">
              {(rowsBySku.get(detailsSku)?.boxes || []).map((box) => (
                <div key={box.id} className="flex items-center justify-between gap-3 py-3 text-xs">
                  <div className="min-w-0">
                    <strong className="block">Box #{box.boxNumber}</strong>
                    <span className="text-[#7b8090]">
                      {formatNumber(effectiveBoxUnits(box))} / {formatNumber(box.expectedUnits)} saved
                      {box.shipment?.tracking ? ` · ${box.shipment.tracking}` : ''}
                    </span>
                  </div>
                  {effectiveBoxUnits(box) > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setFixBox(box);
                        setFixCount(String(effectiveBoxUnits(box)));
                        setFixReason('');
                        setSheet('fix');
                      }}
                      className="shrink-0 rounded-md border border-[#ececef] px-3 py-1.5 font-semibold"
                    >
                      Fix
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setSheet(null)} className="mt-3 h-9 w-full rounded-md border border-[#ececef] text-sm font-medium">Close</button>
          </section>
        </div>
      )}

      {sheet === 'fix' && fixBox && (
        <div className="absolute inset-0 z-30 flex items-end justify-center bg-black/30" role="presentation">
          <section className="w-full rounded-t-xl border border-[#ececef] bg-white p-4 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="fix-box-title">
            <h2 id="fix-box-title" className="text-base font-semibold">Fix box #{fixBox.boxNumber}</h2>
            <p className="mt-1 text-xs text-[#7b8090]">The original receipt stays in the activity trail. This correction is logged with your name and reason.</p>
            <label className="mt-3 block text-xs font-semibold text-[#4b5060]">
              Corrected unit count
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={fixCount}
                onChange={(event) => setFixCount(event.target.value.replace(/\D/g, ''))}
                className="mt-1 h-10 w-full rounded-md border border-[#ececef] px-3 text-sm"
              />
            </label>
            <label className="mt-3 block text-xs font-semibold text-[#4b5060]">
              Reason for amendment
              <input
                type="text"
                value={fixReason}
                onChange={(event) => setFixReason(event.target.value)}
                placeholder="Reason for correction"
                className="mt-1 h-10 w-full rounded-md border border-[#ececef] px-3 text-sm"
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setSheet('boxes')} disabled={saving} className="h-9 rounded-md border border-[#ececef] px-3 text-sm">Cancel</button>
              <button type="button" onClick={handleAmend} disabled={saving} className="h-9 rounded-md bg-[#0f1115] px-4 text-sm font-semibold text-white disabled:opacity-40">{saving ? 'Saving…' : 'Save amendment'}</button>
            </div>
          </section>
        </div>
      )}

      {pagePad && padSku && mode === 'type' && (
        <NumberPadSheet
          label={padSku}
          value={manualBySku[padSku] || ''}
          anchorRef={padAnchorRef}
          onChange={(value) => changeInputAndScroll(padSku, value)}
          onClose={closePad}
          onDone={closePad}
        />
      )}
      {discardOpen && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <section className="w-full max-w-sm rounded-xl border border-[#ececef] bg-white p-4 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="discard-title">
            <h2 id="discard-title" className="text-base font-semibold">Discard pending receiving?</h2>
            <p className="mt-2 text-sm text-[#4b5060]">Unsaved quantities and scanned tags will be lost.</p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setDiscardOpen(false)} className="h-9 rounded-md border border-[#ececef] px-3 text-sm">Keep receiving</button>
              <button type="button" onClick={onClose} className="h-9 rounded-md bg-[#0f1115] px-3 text-sm font-semibold text-white">Discard</button>
            </div>
          </section>
        </div>
      )}
    </div>
  );

  return createPortal(content, document.body);
}

export default SkuReceiveWorkspace;
